import {
  matchEvaluationWorkspacePath,
  projectClientProjectsHref,
  projectWorkspaceHref,
} from '@app/routes/evaluationPlanBreadcrumb';
import type { RouteHandle } from '@app/routes/handle';
import { paths } from '@app/routes/paths';
import { useGetProjectClientQuery } from '@features/projectClients/projectClientsApi';
import { useGetProjectQuery } from '@features/projects/projectsApi';
import { tDynamic } from '@i18n/helpers';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import type { TFunction } from 'i18next';
import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useLocation, useMatches } from 'react-router-dom';

interface Crumb {
  label: ReactNode;
  href: string;
}

/** Inline name placeholder — keeps the trail stable while a name loads. */
function namePlaceholder(width: number) {
  return <Skeleton width={width} sx={{ display: 'inline-block', verticalAlign: 'middle' }} />;
}

function renderCrumbs(crumbs: Crumb[], t: TFunction) {
  if (crumbs.length === 0) return null;
  // Identical look to the page-local trail on /projects (ProjectsPage):
  // default 16px body, "/" separators, hover-underline links, medium last.
  return (
    <Breadcrumbs aria-label={t('nav.breadcrumbAria')}>
      {crumbs.map((crumb, idx) => {
        const isLast = idx === crumbs.length - 1;
        const key = `${crumb.href}#${String(idx)}`;
        if (isLast) {
          return (
            <Typography
              key={key}
              aria-current="page"
              color="text.primary"
              sx={{ fontWeight: 500 }}
            >
              {crumb.label}
            </Typography>
          );
        }
        return (
          <Link
            key={key}
            component={RouterLink}
            to={crumb.href}
            underline="hover"
            color="inherit"
            sx={{ display: 'inline-flex', alignItems: 'center' }}
          >
            {crumb.label}
          </Link>
        );
      })}
    </Breadcrumbs>
  );
}

/**
 * Derives a breadcrumb trail from the active route tree. Each route's
 * `handle.breadcrumbKey` (or `handle.titleKey` as fallback) becomes a crumb;
 * `breadcrumbKey: null` skips the match entirely. A fully-rendered
 * `handle.breadcrumb` ReactNode wins over both.
 *
 * The last crumb is rendered as the current page; everything before is a
 * quiet link. Names still loading render as skeletons — never fallback text
 * that would swap once the query resolves.
 */
export function AppBreadcrumbs() {
  const matches = useMatches();
  const { pathname } = useLocation();
  const { t } = useTranslation();

  const planCtx = matchEvaluationWorkspacePath(pathname);
  const scopeProjectId = planCtx?.projectId ?? '';
  const scopeProjectClientFromUrl = planCtx?.projectClientId;

  const { data: scopeProject, isLoading: projectLoading } = useGetProjectQuery(scopeProjectId, {
    skip: !scopeProjectId,
  });
  const resolvedProjectClientId =
    scopeProjectClientFromUrl ?? scopeProject?.projectClientId ?? '';
  const { data: scopeProjectClient, isLoading: clientLoading } = useGetProjectClientQuery(
    resolvedProjectClientId,
    { skip: !scopeProjectId || !resolvedProjectClientId },
  );

  const workspacePrefix = (projectId: string): Crumb[] => {
    const prefix: Crumb[] = [];
    if (resolvedProjectClientId && projectId) {
      prefix.push({ label: t('nav.clients'), href: paths.projectClients });
      prefix.push({
        label:
          scopeProjectClient?.name ??
          (clientLoading ? namePlaceholder(72) : t('workspace.breadcrumbs.clientFallback')),
        href: projectClientProjectsHref(resolvedProjectClientId),
      });
      prefix.push({
        label:
          scopeProject?.name ??
          (projectLoading ? namePlaceholder(88) : t('routes.projectWorkspace')),
        href: projectWorkspaceHref(resolvedProjectClientId, projectId),
      });
    } else if (projectId && projectLoading) {
      // Flat URL (`/projects/...`): the owning client is still resolving —
      // hold the trail's shape with placeholders instead of popping in later.
      prefix.push({ label: t('nav.clients'), href: paths.projectClients });
      prefix.push({ label: namePlaceholder(72), href: paths.projectClients });
      prefix.push({ label: namePlaceholder(88), href: paths.projectClients });
    }
    return prefix;
  };

  if (planCtx) {
    // 1 Projekt = 1 Plan: the project name IS the plan crumb — no
    // intermediate evaluations level.
    return renderCrumbs(workspacePrefix(planCtx.projectId), t);
  }

  const crumbs: Crumb[] = [];
  for (const match of matches) {
    const handle = match.handle as RouteHandle | undefined;
    if (!handle) continue;
    if (handle.breadcrumbKey === null) continue;

    let label: ReactNode;
    if (handle.breadcrumb !== undefined) {
      label = handle.breadcrumb;
    } else {
      const key = handle.breadcrumbKey ?? handle.titleKey;
      if (!key) continue;
      label = tDynamic(t, key);
    }
    crumbs.push({ label, href: match.pathname });
  }

  return renderCrumbs(crumbs, t);
}
